import React from "react";
import { Box } from "@mui/material";
import SabiasQue from "../SabiasQue";

// Template29: Página Día 2 - Pabellón Dorado y Ryoan-ji (Kioto)
// Contenido desde page.texts, page.images y page.canvasImages (servidor).
const Template29 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const isSmallScreen = window.innerWidth <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItem = (html) => {
		if (!html) return null;
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const p = doc.querySelector("p");
		if (!p) return null;
		const text = (p.textContent ?? p.innerText ?? "").trim();
		const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
		if (emojiMatch)
			return { icono: emojiMatch[1], texto: emojiMatch[2].trim() };
		return null;
	};

	const headerText = page?.texts?.[0]?.content;
	const introText = page?.texts?.[1]?.content;
	const heroTitleShort = page?.texts?.[2]?.content;
	const heroTitleLong = page?.texts?.[3]?.content;
	const panOroText = page?.texts?.[4]?.content;
	const pabellonTitle = page?.texts?.[5]?.content;
	const pabellonText = page?.texts?.[6]?.content;
	const entradaText = page?.texts?.[7]?.content;
	const transporteText = page?.texts?.[8]?.content;
	const ryoanjiTitle = page?.texts?.[9]?.content;
	const ryoanjiText = page?.texts?.[10]?.content;
	const sabiasQue1 = parseSabiasQueItem(page?.texts?.[11]?.content);
	const sabiasQue2 = parseSabiasQueItem(page?.texts?.[12]?.content);
	const sabiasQue3 = parseSabiasQueItem(page?.texts?.[13]?.content);
	const sabiasQue4 = parseSabiasQueItem(page?.texts?.[14]?.content);
	const sabiasQue5 = parseSabiasQueItem(page?.texts?.[15]?.content);

	const temploDoradoImage = getImageSrc("templo-dorado");
	const temploDorado2Image = getImageSrc("templo-dorado-2");
	const temploRyoanjiImage = getImageSrc("templo-ryoanji");
	const logoImage = getCanvasImageUrl("logo");
	const temploDoradoSticker3 = getCanvasImageUrl("templo-dorado-sticker3");
	const temploDoradoSticker2 = getCanvasImageUrl("templo-dorado-sticker2");
	const ryoanjiSticker = getCanvasImageUrl("ryoanji-sticker");

	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
		color: contentColor,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#5ba374",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{headerText}
				{!isSmallScreen && logoImage && (
					<Box
						component="img"
						src={logoImage}
						alt="logo"
						sx={{
							width: 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{temploDoradoImage && (
					<Box
						component="img"
						src={temploDoradoImage}
						alt="Templo Dorado"
						sx={{
							width: "100%",
							height: 194,
							objectFit: "cover",
						}}
					/>
				)}
				{(heroTitleShort || heroTitleLong) && (
					<Box
						component="h1"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "32px",
							textAlign: "center",
							position: "absolute",
							bottom: "2px",
							margin: "10px",
							left: "20px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{isSmallScreen ? heroTitleShort : heroTitleLong}
					</Box>
				)}
				{temploDoradoSticker3 && (
					<Box
						component="img"
						src={temploDoradoSticker3}
						alt="TemploDoradoSticker"
						sx={{
							width: 90,
							position: "absolute",
							bottom: 60,
							left: 25,
						}}
					/>
				)}
			</Box>

			<Box data-block-id="text-1" sx={{ padding: "8px 12px 10px", position: "relative" }}>
				{introText && (
					<Box
						component="p"
						sx={{
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							fontSize: "13px",
							color: "#333",
							margin: "0 !important",
							padding: 0,
							marginBottom: "0px !important",
						}}
					>
						{introText}
					</Box>
				)}
			</Box>

			<Box
				sx={{
					padding: "0 12px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					marginTop: "8px",
				}}
			>
				<Box sx={{ display: "flex", gap: "16px", position: "relative" }}>
					<Box sx={{ display: "flex", flexDirection: "column", width: "40%" }}>
						{temploDorado2Image && (
							<Box
								component="img"
								src={temploDorado2Image}
								alt="TemploDorado2"
								sx={{
									width: "100%",
									height: 100,
									borderRadius: "12px",
									objectFit: "cover",
								}}
							/>
						)}
						<Box sx={{ marginTop: isSmallScreen ? "140px" : 0 }}>
							{panOroText && (
								<Box
									sx={{
										position: "absolute",
										bottom: isSmallScreen ? "80px" : "-20px",
										left: isSmallScreen ? "-5px" : "40px",
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: "11px",
										fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
										color: contentColor,
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 130,
										zIndex: 6,
									}}
								>
									{panOroText}
								</Box>
							)}
							{temploDoradoSticker2 && (
								<Box
									component="img"
									src={temploDoradoSticker2}
									alt="TemploDoradoSticker2"
									sx={{
										width: 100,
										position: "absolute",
										left: "-30px",
										bottom: isSmallScreen ? 0 : "-60px",
										zIndex: 5,
									}}
								/>
							)}
						</Box>
					</Box>
					{sabiasQue1 && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "-10px" : "60px",
								left: isSmallScreen ? "46%" : "34%",
								zIndex: 30,
							}}
						>
							<SabiasQue icono={sabiasQue1.icono} texto={sabiasQue1.texto} />
						</Box>
					)}
					{isSmallScreen && sabiasQue2 && (
						<Box
							sx={{
								position: "absolute",
								bottom: "0px",
								left: "60%",
								zIndex: 10,
							}}
						>
							<SabiasQue icono={sabiasQue2.icono} texto={sabiasQue2.texto} />
						</Box>
					)}
					{sabiasQue3 && (
						<Box
							sx={{
								position: "absolute",
								bottom: isSmallScreen ? "0px" : "12px",
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 20,
							}}
						>
							<SabiasQue icono={sabiasQue3.icono} texto={sabiasQue3.texto} />
						</Box>
					)}
					{sabiasQue4 && (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: isSmallScreen ? "32%" : "34%",
								zIndex: 1000,
							}}
						>
							<SabiasQue icono={sabiasQue4.icono} texto={sabiasQue4.texto} />
						</Box>
					)}

					<Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
						<Box>
							{pabellonTitle && (
								<Box component="h3" sx={h3}>
									{pabellonTitle}
								</Box>
							)}
							{pabellonText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
									dangerouslySetInnerHTML={{ __html: pabellonText }}
								/>
							)}
						</Box>
						{entradaText && (
							<Box
								sx={{
									backgroundColor: "#d9e6de",
									border: "3px solid #5ba374",
									borderRadius: "12px",
									fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
									width: "100%",
									padding: "4px 10px",
									marginTop: "12px",
								}}
							>
								<Box
									component="p"
									sx={{ ...p, margin: "0 !important", fontSize: "11px" }}
								>
									{entradaText}
								</Box>
							</Box>
						)}
					</Box>
				</Box>

				{(transporteText || ryoanjiSticker) && (
					<Box
						sx={{
							backgroundColor: "#d9e6de",
							padding: "12px",
							borderRadius: "12px",
							display: "flex",
							gap: "16px",
							alignItems: "center",
							position: "relative",
							marginTop: isSmallScreen ? 0 : "30px",
						}}
					>
						<Box sx={{ paddingLeft: "8px" }}>
							{transporteText && (
								<Box
									component="p"
									sx={{
										...p,
										margin: "0 !important",
										marginBottom: "0px !important",
									}}
									dangerouslySetInnerHTML={{ __html: transporteText }}
								/>
							)}
						</Box>
						{ryoanjiSticker && (
							<Box
								component="img"
								src={ryoanjiSticker}
								alt="RyoanjiSticker"
								sx={{ width: isSmallScreen ? 80 : 100 }}
							/>
						)}
						{!isSmallScreen && sabiasQue5 && (
							<Box
								sx={{
									position: "absolute",
									top: "-12px",
									right: "50%",
									zIndex: 10,
								}}
							>
								<SabiasQue icono={sabiasQue5.icono} texto={sabiasQue5.texto} />
							</Box>
						)}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						gap: "16px",
						position: "relative",
						marginBottom: isSmallScreen ? "20px" : 0,
					}}
				>
					<Box sx={{ width: "60%", display: "flex", flexDirection: "column" }}>
						{ryoanjiTitle && (
							<Box>
								<Box component="h3" sx={h3}>
									{ryoanjiTitle}
								</Box>
								{ryoanjiText && (
									<Box
										component="p"
										sx={{
											...p,
											margin: "0 !important",
											marginBottom: "0px !important",
										}}
									>
										{ryoanjiText}
									</Box>
								)}
							</Box>
						)}
					</Box>
					<Box sx={{ display: "flex", flexDirection: "column", width: "40%" }}>
						{temploRyoanjiImage && (
							<Box
								component="img"
								src={temploRyoanjiImage}
								alt="TemploRyoanji"
								sx={{
									width: "100%",
									height: 100,
									borderRadius: "12px",
									objectFit: "cover",
								}}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template29.thumbnail = "/images/template-1.png";

export default Template29;
